import {
  WSButton,
  WSButtons,
  WSFlexGrid,
  WSForm,
  WSSelect,
  WSText,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import React from "react";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";
import { SetupFlowRouteComponentProps } from "../../components/SetupFlow/SetupFlow";
import { WSErrorMessage } from "../../components/WSErrorMessage/WSErrorMessage";
import { US_STATES_OPTIONS } from "../../constants/user";
import { useWSMutation } from "../../query/helpers";
import { useUserId } from "../../query/hooks/helpers";
import {
  useUpdateActivity,
  useUpdateMemberProfile,
  useUpdateUserProfile
} from "../../query/users/mutations";
import { useActivities, useMemberProfile } from "../../query/users/queries";
import { get1099SetupProgress } from "../../query/users/selectors";
import { WSQueries } from "../../query/WSQuery";
import { ErrorContextKey } from "../../services/platform";
import {
  companyNameValidator,
  validatorAddressLine1,
  validatorAddressLine2,
  validatorEmail,
  validatorSSN,
  validatorTaxId,
  validatorZipCode
} from "../../utils/validators";
import { NEC1099FilingContainer } from "../components/NEC1099FilingContainer";

import styles from "./styles.module.scss";

interface ConfirmPayerInfoFormValues {
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  phoneNumber: string;
  tin?: string;
  ssn?: string;
}

export const ConfirmPayerInfo: React.FC<SetupFlowRouteComponentProps> = ({
  onBack,
  onNext
}) => {
  const history = useHistory();
  const userId = useUserId();
  const qMember = useMemberProfile(userId);
  const qActivity = useActivities(userId);
  const [
    updateMemberProfile,
    updateMemberProfileMeta
  ] = useUpdateMemberProfile();
  const [updateActivity] = useUpdateActivity(userId);
  const [confirmPayerInfo, confirmPayerInfoMeta] = useWSMutation(async data => {
    await updateMemberProfile(
      {
        memberId: userId,
        profile: {
          ...(data.name
            ? {
                company: {
                  legalBusinessName: data.name
                }
              }
            : {}),
          ...(data.ssn ? { ssn: data.ssn } : {}),
          ...(data.tin
            ? {
                company: {
                  taxId: data.tin
                }
              }
            : {}),
          address: {
            addressLine1: data.addressLine1,
            addressLine2: data.addressLine2,
            city: data.city,
            state: data.state,
            postalCode: data.zipCode,
            country: qMember.data?.profile.address?.country ?? "US"
          }
        }
      },
      {
        onSuccess: async () => {
          const nec1099SetupProgress = get1099SetupProgress(qActivity.data);
          await updateActivity(
            {
              flows: {
                nec1099Setup: {
                  ...nec1099SetupProgress,
                  currentStep: 2
                }
              }
            },
            {
              onSuccess: () => {
                onNext();
              }
            }
          );
        }
      }
    );
  });

  const onFormSubmit = async (data: ConfirmPayerInfoFormValues) => {
    updateMemberProfileMeta.reset();

    await confirmPayerInfo(data);
  };

  return (
    <NEC1099FilingContainer title="Payer information">
      <WSQueries queries={{ qMember }}>
        {({ qMember: { data: member } }) => {
          const hasSubmittedSSN =
            member.profile.ssnLastFourProvided || member.profile.ssnProvided;
          const { address, company } = member.profile;
          return (
            <>
              <WSText.Heading2 mb="S">
                Confirm payer information
              </WSText.Heading2>
              <WSText.Paragraph color="grey700">
                Does this information look right? If you make changes here, they
                will appear on your company profile and 1099-NEC forms.
              </WSText.Paragraph>
              <WSForm<ConfirmPayerInfoFormValues>
                defaultValues={{
                  name: company?.legalBusinessName ?? company?.name,
                  addressLine1: address?.addressLine1,
                  addressLine2: address?.addressLine2,
                  city: address?.city,
                  state: address?.state,
                  zipCode: address?.postalCode,
                  ...(company?.taxId && { tin: company?.taxId }),
                  ...(!hasSubmittedSSN && { ssn: "" })
                }}
                validationSchema={Yup.object().shape({
                  name: companyNameValidator,
                  addressLine1: validatorAddressLine1,
                  addressLine2: validatorAddressLine2,
                  city: Yup.string().required("Required"),
                  state: Yup.string().required("Required"),
                  zipCode: validatorZipCode,
                  supportEmail: validatorEmail,
                  ...(company?.taxId
                    ? { tin: validatorTaxId.required("Required") }
                    : {}),
                  ...(!hasSubmittedSSN
                    ? { ssn: validatorSSN.required("Required") }
                    : {})
                })}
                onSubmit={onFormSubmit}
              >
                <WSForm.Field
                  mt="M"
                  name="name"
                  component={WSTextInput}
                  label="Name"
                />

                <WSForm.Field
                  mt="M"
                  name="addressLine1"
                  component={WSTextInput}
                  label="Address line 1"
                />
                <WSForm.Field
                  mt="M"
                  name="addressLine2"
                  component={WSTextInput}
                  label="Address line 2 (optional)"
                />
                <WSFlexGrid container mt="M">
                  <WSFlexGrid item mobile="12" tablet="12" desktop="6">
                    <WSForm.Field
                      mt="M"
                      name="city"
                      component={WSTextInput}
                      label="City"
                    />
                  </WSFlexGrid>
                  <WSFlexGrid item mobile="12" tablet="6" desktop="3">
                    <WSForm.Field
                      mt="M"
                      name="state"
                      component={WSSelect}
                      componentProps={{
                        options: US_STATES_OPTIONS,
                        placeholder: "Select"
                      }}
                      label="State"
                    />
                  </WSFlexGrid>
                  <WSFlexGrid item mobile="12" tablet="6" desktop="3">
                    <WSForm.Field
                      mt="M"
                      name="zipCode"
                      component={WSTextInput}
                      componentProps={{
                        formatOptions: {
                          numericOnly: true,
                          blocks: [5]
                        }
                      }}
                      label="Zip code"
                    />
                  </WSFlexGrid>
                </WSFlexGrid>

                {company?.taxId ? (
                  <WSForm.Field
                    mt="M"
                    name="tin"
                    label="TIN (taxpayer identification number)"
                    component={WSTextInput}
                    componentProps={{
                      formatOptions: {
                        numericOnly: true,
                        blocks: [2, 7],
                        delimiters: ["-"],
                        delimiterLazyShow: true
                      },
                      fsExclude: true
                    }}
                  />
                ) : !hasSubmittedSSN ? (
                  <WSForm.Field
                    mt="M"
                    name="ssn"
                    label="SSN"
                    component={WSTextInput}
                    componentProps={{
                      type: "password",
                      allowToShowPassword: true,
                      formatOptions: {
                        numericOnly: true,
                        blocks: [9]
                      }
                    }}
                    mb="XS"
                  />
                ) : null}

                <WSErrorMessage
                  mt="M"
                  contextKey={ErrorContextKey.Other}
                  error={updateMemberProfileMeta.error}
                />

                <WSButtons mt="XL">
                  <WSButton.Secondary
                    className={styles.actionBtn}
                    onClick={onBack}
                  >
                    Back
                  </WSButton.Secondary>
                  <WSForm.SubmitButton
                    className={styles.actionBtn}
                    data-testid="confirmPayerInfo"
                    loading={confirmPayerInfoMeta.isLoading}
                  >
                    Continue
                  </WSForm.SubmitButton>
                </WSButtons>
              </WSForm>
            </>
          );
        }}
      </WSQueries>
    </NEC1099FilingContainer>
  );
};
